import { gql, useQuery } from '@apollo/client';
import classNames from 'classnames';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useAppSelector from '../app/redux/useAppSelector';
import Toast from '../components/Toast';
import type {
  TopicViewQuery,
  TopicViewQueryVariables,
} from '../graphql/generated/graphqlTypes';
import { TopicPrivacy } from '../graphql/generated/graphqlTypes';
import UIActions from '../ui/UIActions';
import PageMeta from '../utils/PageMeta';
import handleApolloError from '../utils/error/handleApolloError';
import useErrorRedirectEffect from '../utils/error/useErrorRedirectEffect';
import useSlug from '../utils/router/useSlug';
import { useRouter } from '../utils/router/v3Bridge';
import topicPath from '../utils/url/topicPath';
import useIsEditingInPlace from '../utils/useIsEditingInPlace';
import TopicAccess from './TopicAccess';
import styles from './TopicView.module.scss';
import TopicViewHeader from './TopicViewHeader';
import TopicPosts from './posts/TopicPosts';
import topicPolicyCheck, { TopicPolicyAction } from './topicPolicyCheck';
import { useAllTopicMap } from './useAllTopics';
import {
  useRemoveTopicUser,
  useUpdateTopicUser,
} from './useTopicUserMutations';
import TopicUsersPanel from './users/TopicUsersPanel';
import sortTopic from './utils/sortTopic';

const TOPIC_VIEW_TOPIC_FRAGMENT = gql`
  fragment TopicView_topic on Topic {
    id
    name
    children {
      ...sortTopic_topic
    }
    ...TopicViewHeader_topic
  }
  ${sortTopic.fragments.topic}
  ${TopicViewHeader.fragments.topic}
`;

const TOPIC_QUERY = gql`
  query TopicView($id: ID!) {
    topic(id: $id) {
      ...TopicView_topic
    }
  }
  ${TOPIC_VIEW_TOPIC_FRAGMENT}
`;

const useMembershipEffect = () => {
  const dispatch = useDispatch();
  const [topicId] = useSlug('topicSlug');
  const router = useRouter();
  const removeTopicUser = useRemoveTopicUser();
  const updateTopicUser = useUpdateTopicUser();

  const { grantAccessTo, declineAccessTo } = queryString.parse(
    document.location.search,
  ) as {
    grantAccessTo: string;
    declineAccessTo: string;
  };

  if (grantAccessTo) {
    router.replace(document.location.pathname);

    updateTopicUser(
      { id: topicId },
      { id: grantAccessTo },
      'requestor',
      'member',
    )
      .then(({ data }) => {
        if (data?.user?.name) {
          dispatch(
            UIActions.pushToast(
              ({ onDismiss }: { onDismiss: () => void }) => (
                <Toast
                  onDismiss={onDismiss}
                  title="New topic member added"
                  message={`${data.user.name} has been added as a member of this topic.`}
                />
              ),
              7000,
            ),
          );
        }
      })
      .catch((error) => {
        handleApolloError(error, 'NOT_FOUND', () =>
          dispatch(
            UIActions.pushToast(
              ({ onDismiss }: { onDismiss: () => void }) => (
                <Toast
                  onDismiss={onDismiss}
                  title="Topic member not added"
                  message="Request has been rejected by another member of this topic."
                />
              ),
              7000,
            ),
          ),
        );
      });
  }

  if (declineAccessTo) {
    router.replace(document.location.pathname);

    removeTopicUser({ id: topicId }, { id: declineAccessTo }).then(
      ({ data }) => {
        if (data?.user?.name) {
          dispatch(
            UIActions.pushToast(
              {
                title: 'Membership request declined',
                message: `${data.user.name}'s request has been removed.`,
              },
              7000,
            ),
          );
        }
      },
    );
  }
};

const TopicView = () => {
  const [topicId] = useSlug('topicSlug');
  const topicMap = useAllTopicMap();
  const user = useAppSelector((state) => state.session.user);
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useIsEditingInPlace();
  const router = useRouter();

  useMembershipEffect();

  useEffect(
    () => () => {
      dispatch(UIActions.closeRightPanel());
    },
    [dispatch],
  );

  const { error, data } = useQuery<TopicViewQuery, TopicViewQueryVariables>(
    TOPIC_QUERY,
    { variables: { id: topicId } },
  );

  useErrorRedirectEffect({ noun: 'topic', error });

  const topicForPermissionCheck = topicMap[topicId];

  const topic = data && {
    ...data.topic,
    children: [...data.topic.children].sort(sortTopic),
  };

  const topicPathUrl = topic ? topicPath(topic) : null;

  useEffect(() => {
    if (topicPathUrl && router.location.state?.new) {
      if (isEditing) {
        dispatch(UIActions.closeRightPanel());
        return;
      }
      if (
        topic?.privacy === TopicPrivacy.Secret ||
        topic?.privacy === TopicPrivacy.Private
      ) {
        router.push(topicPathUrl);
        dispatch(UIActions.openRightPanel(TopicUsersPanel));
      }
    }
  }, [dispatch, isEditing, router, topic?.privacy, topicPathUrl]);

  if (topic == null || topicForPermissionCheck == null) return null;

  const canViewPosts = topicPolicyCheck(
    TopicPolicyAction.ViewPost,
    topicForPermissionCheck,
    user,
  );

  return (
    <div
      id="content"
      key={topic.id}
      className={classNames(
        styles.container,
        {
          [styles.isEditing]: isEditing,
        },
        'no-padding-x no-padding-y',
      )}
    >
      <PageMeta title={`${topic.name} - Slab`} />
      <TopicViewHeader
        topic={topic}
        editing={isEditing}
        setIsEditing={setIsEditing}
      />
      {canViewPosts ? (
        <TopicPosts topicId={topic.id} />
      ) : (
        <TopicAccess topic={topicForPermissionCheck} />
      )}
    </div>
  );
};

export default TopicView;
